import { useMemo, useRef, useState,useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Popover,
  Collapse
} from "@mui/material"
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  Warning as WarningIcon,
  AccountTree as DependencyIcon,
  Task as TaskIcon,
} from "@mui/icons-material"
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, addMonths, parseISO, format, differenceInDays, } from "date-fns"

import IssueTooltip from "./roadmapIssuetooltips"
import DependencyLines from "./roadDependencyLines"
import { ISSUE_TYPES, STATUS_NAMES, STATUS_COLORS } from "./roadmap"

const GanttChart = ({
  organizedIssues,
  expandedEpics,
  expandedStories,
  timeRange,
  timelineView,
  dependencies,
  issueDependencies,
  taskDetailsPanelOpen,
  onIssueNameClick,
  onMenuClick,
  onToggleEpicExpansion,
  onToggleStoryExpansion,
  getIssueDependencyInfo,
  sidebarOpen 
}) => {
  const svgRef = useRef(null)
  const [svgWidth, setSvgWidth] = useState(0)
  const [scrollTop, setScrollTop] = useState(0)
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const [popoverIssue, setPopoverIssue] = useState(null)
  const scrollContainerRef = useRef(null)

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop)
  }

  // Calculate time columns
  const timeColumns = useMemo(() => {
    const columns = []
    let currentDate = new Date(timeRange.start)

    while (currentDate <= timeRange.end) {
      if (timelineView === "weekly") {
        const weekStart = startOfWeek(currentDate)
        const weekEnd = endOfWeek(currentDate)
        columns.push({
          date: new Date(currentDate),
          label: format(weekStart, "MMM dd"),
          startDate: weekStart,
          endDate: weekEnd,
        })
        currentDate = addWeeks(currentDate, 1)
      } else if (timelineView === "quarterly") {
        const quarterStart = startOfMonth(currentDate)
        const quarterEnd = endOfMonth(addMonths(currentDate, 2))
        columns.push({
          date: new Date(currentDate),
          label: format(currentDate, "QQQ yyyy"),
          startDate: quarterStart,
          endDate: quarterEnd,
        })
        currentDate = addMonths(currentDate, 3)
      } else {
        columns.push({
          date: new Date(currentDate),
          label: format(currentDate, "MMM yyyy"),
          startDate: startOfMonth(currentDate),
          endDate: endOfMonth(currentDate),
        })
        currentDate = addMonths(currentDate, 1)
      }
    }

    return columns
  }, [timeRange, timelineView])
  // Measure SVG width for accurate dependency line calculation
  useEffect(() => {
    const updateSvgWidth = () => {
      if (svgRef.current) {
        const width = svgRef.current.getBoundingClientRect().width
        if (width > 0) {
          setSvgWidth(width)
        }
      }
    }

    updateSvgWidth()
    const timeoutId = setTimeout(updateSvgWidth, 100)
    const timeoutId2 = setTimeout(updateSvgWidth, 300)

    window.addEventListener("resize", updateSvgWidth)

    return () => {
      window.removeEventListener("resize", updateSvgWidth)
      clearTimeout(timeoutId)
      clearTimeout(timeoutId2)
    }
  }, [taskDetailsPanelOpen, sidebarOpen, organizedIssues, expandedEpics, expandedStories])
  // Get visible issue list
  const getVisibleIssueList = () => {
    const visibleIssues = []

    organizedIssues.forEach((epic) => {
      visibleIssues.push(epic)

      const isEpicExpanded = Boolean(expandedEpics[epic.id] || expandedEpics[String(epic.id)])
      if (isEpicExpanded && epic.children) {
        epic.children.forEach((child) => {
          visibleIssues.push(child)

          const isStory = Number(child.issueType) === 3 || Number(child.issueType) === 4 || String(child.issueType).toLowerCase() === "story"
          const isStoryExpanded = Boolean(expandedStories[child.id] || expandedStories[String(child.id)])
          if (isStory && isStoryExpanded && child.children) {
            child.children.forEach((grandChild) => {
              visibleIssues.push(grandChild)
            })
          }
        })
      }
    })

    return visibleIssues
  }

  // Calculate issue position
  const calculateGanttPosition = (issue, parentEndDate = null) => {
    if (!issue.startDate || !issue.endDate) {
      return {
        left: "0%",
        width: "15%",
        startDate: "No date",
        endDate: "No date",
        duration: "N/A",
        isOverdue: false,
        overdueBy: null,
        overdueInfo: null,
        noDates: true,
      }
    }

    try {
      const issueStart = parseISO(issue.startDate)
      const issueEnd = parseISO(issue.endDate)
      const today = new Date()
      const totalDays = Math.max(1, differenceInDays(timeRange.end, timeRange.start))
      const startOffset = differenceInDays(issueStart, timeRange.start)
      const duration = Math.max(1, differenceInDays(issueEnd, issueStart) + 1)

      const leftPercent = Math.max(0, Math.min(95, (startOffset / totalDays) * 100))
      const widthPercent = Math.min(100 - leftPercent, Math.max(3, (duration / totalDays) * 100))

      const isOverdue = issueEnd < today && Number(issue.status) !== 3 && Number(issue.status) !== 5
      const exceedsParent = parentEndDate && issueEnd > parseISO(parentEndDate)

      let overdueInfo = null
      if (isOverdue || exceedsParent) {
        const referenceDate = exceedsParent ? parseISO(parentEndDate) : today
        const overdueDays = differenceInDays(issueEnd, referenceDate)
        const overdueStartOffset = differenceInDays(referenceDate, timeRange.start)
        const overdueLeftPercent = Math.max(0, (overdueStartOffset / totalDays) * 100)
        const overdueWidthPercent = Math.min(100 - overdueLeftPercent, (overdueDays / totalDays) * 100)

        overdueInfo = {
          left: `${overdueLeftPercent}%`,
          width: `${Math.max(overdueWidthPercent, 1)}%`,
          days: overdueDays,
        }
      }

      return {
        left: `${leftPercent}%`,
        width: `${Math.max(widthPercent, 1)}%`,
        startDate: format(issueStart, "MMM dd, yyyy"),
        endDate: format(issueEnd, "MMM dd, yyyy"),
        duration: `${duration} days`,
        isOverdue: isOverdue || exceedsParent,
        overdueBy: isOverdue
          ? `${differenceInDays(today, issueEnd)} days`
          : exceedsParent
            ? `${differenceInDays(issueEnd, parseISO(parentEndDate))} days beyond parent`
            : null,
        overdueInfo: overdueInfo,
        noDates: false,
      }
    } catch (err) {
      return {
        left: "0%",
        width: "15%",
        startDate: "Invalid date",
        endDate: "Invalid date",
        duration: "N/A",
        isOverdue: false,
        overdueBy: null,
        overdueInfo: null,
        noDates: true,
      }
    }
  }

  const hasOverdueIssues = (issue, parentEndDate = null) => {
    const position = calculateGanttPosition(issue, parentEndDate)
    if (position?.isOverdue) {
      return true
    }

    if (issue.children && issue.children.length > 0) {
      for (const child of issue.children) {
        const childPosition = calculateGanttPosition(child, issue.endDate)
        if (childPosition?.isOverdue) {
          return true
        }

        if (child.children && child.children.length > 0) {
          for (const grandChild of child.children) {
            const grandChildPosition = calculateGanttPosition(grandChild, child.endDate)
            if (grandChildPosition?.isOverdue) {
              return true
            }
          }
        }
      }
    }

    return false
  }


  // Render issue bar
  const renderIssueBar = (issue, position, parentEndDate = null) => {
    const IssueIcon = ISSUE_TYPES[issue.issueType]?.icon || TaskIcon
    const issueColor = ISSUE_TYPES[issue.issueType]?.color || "#1976d2"

    const handleIssueBarClick = (event, issue) => {
      event.stopPropagation()
      setPopoverAnchor(event.currentTarget)
      setPopoverIssue(issue)
    }

    const handlePopoverClose = () => {
      setPopoverAnchor(null)
      setPopoverIssue(null)
    }

    return (
      <Box sx={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center" }}>
        {/* Start Date - Left side (pointer-events none so it doesn't block row clicks) */}
        {!position.noDates && issue.startDate && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {position.isOverdue && <WarningIcon sx={{ color: "#d32f2f", fontSize: 16 }} />}
            <Typography
              variant="caption"
              sx={{
                color: position.isOverdue ? "#d32f2f" : "#666",
                fontSize: "10px",
                fontWeight: "bold",
                backgroundColor: "rgba(255,255,255,0.9)",
                padding: "2px 4px",
                borderRadius: 1,
                whiteSpace: "nowrap",
                mt: "40px",
              }}
            >
              {format(parseISO(issue.startDate), "MMM dd")}
            </Typography>
          </Box>
        )}

        {/* Main issue bar */}
        <Tooltip
          title={<IssueTooltip issue={issue} position={position} />}
          arrow
          placement="top"
          enterDelay={300}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "transparent",
                maxWidth: "none",
              },
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              height: 32,
              backgroundColor: position.noDates ? "#9e9e9e" : issueColor,
              borderStyle: position.noDates ? "dashed" : "solid",
              borderWidth: position.noDates ? "1px" : position.isOverdue ? "2px" : "0px",
              borderColor: position.isOverdue ? "#d32f2f" : position.noDates ? "#757575" : "transparent",
              borderRadius: 2,
              left: position.left,
              width: position.width,
              opacity: position.noDates ? 0.7 : 0.9,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": {
                opacity: 1,
                height: 36,
                zIndex: 5,
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              },
              transition: "all 0.2s ease",
              zIndex: 2,
            }}
            onClick={(e) => handleIssueBarClick(e, { ...issue, position })}
          >
            <Typography variant="caption" sx={{ color: "white", fontSize: "11px", fontWeight: "bold", px: 0.5 }} noWrap>
              {issue.key} {position.noDates ? "(No date)" : ""}
            </Typography>
          </Box>
        </Tooltip>

        {/* Overdue/Exceeding portion */}
        {position.isOverdue && position.overdueInfo && (
          <Tooltip title={`Overdue: ${position.overdueBy}`} arrow placement="top">
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                height: 32,
                backgroundColor: "#d32f2f",
                borderRadius: 2,
                left: position.overdueInfo.left,
                width: position.overdueInfo.width,
                opacity: 0.95,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px solid #b71c1c",
                "&:hover": {
                  opacity: 1,
                  height: 36,
                  zIndex: 6,
                  boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                },
                transition: "all 0.2s ease",
                zIndex: 3,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: -2,
                  top: -2,
                  right: -2,
                  bottom: -2,
                  background:
                    "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.3) 3px, rgba(255,255,255,0.3) 6px)",
                  borderRadius: 2,
                  pointerEvents: "none",
                },
              }}
              onClick={(e) => handleIssueBarClick(e, { ...issue, position })}
            >
              <Typography variant="caption" sx={{ color: "white", fontSize: "10px", fontWeight: "bold" }}>
                OVERDUE
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* End Date - Right side (pointer-events none so it doesn't block row clicks) */}
        {!position.noDates && issue.endDate && (
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: position.isOverdue ? "#d32f2f" : "#666",
                fontSize: "10px",
                fontWeight: "bold",
                backgroundColor: "rgba(255,255,255,0.9)",
                padding: "2px 4px",
                borderRadius: 1,
                whiteSpace: "nowrap",
                mt: "24px",
              }}
            >
              {format(parseISO(issue.endDate), "MMM dd")}
            </Typography>
            {position.isOverdue && <WarningIcon sx={{ color: "#d32f2f", fontSize: 16 }} />}
          </Box>
        )}

        {/* Popover */}
        <Popover
          open={Boolean(popoverAnchor && popoverIssue?.id === issue.id)}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          sx={{ mt: 1 }}
        >
          {popoverIssue && (
            <Box>
              <IssueTooltip issue={popoverIssue} position={popoverIssue.position} />
              <Box sx={{ p: 1, borderTop: "1px solid #eee", textAlign: "right" }}>
                <Chip
                  label="View Full Details"
                  size="small"
                  color="primary"
                  clickable
                  onClick={() => {
                    handlePopoverClose()
                    onIssueNameClick(popoverIssue)
                  }}
                  sx={{ cursor: "pointer" }}
                />
              </Box>
            </Box>
          )}
        </Popover>
      </Box>
    )
  }

  // Render dependency chips
  const renderDependencyChips = (issue) => {
    const chips = []
    const processedDependencies = new Set()

    const createDependencyKey = (sourceId, targetId) => {
      return `${Math.min(sourceId, targetId)}-${Math.max(sourceId, targetId)}`
    }

    const dependencyInfo = getIssueDependencyInfo(issue.id)

    if (dependencyInfo.blocks && dependencyInfo.blocks.length > 0) {
      dependencyInfo.blocks.forEach((dep) => {
        const dependencyKey = createDependencyKey(issue.id, dep.issue.id)

        if (!processedDependencies.has(dependencyKey)) {
          processedDependencies.add(dependencyKey)

          const linkTypeText =
            dep.linkType === "blocks"
              ? "blocks"
              : dep.linkType === "is blocked by"
                ? "blocks"
                : dep.linkType === "relates to"
                  ? "relates to"
                  : dep.linkType === "duplicates"
                    ? "duplicates"
                    : dep.linkType === "is duplicated by"
                      ? "duplicates"
                      : "blocks"

          chips.push(
            <Tooltip
              key={`blocks-${dep.issue.id}`}
              title={`${linkTypeText} ${dep.issue.key}: ${dep.issue.summary}`}
              arrow
            >
              <Chip
                size="small"
                icon={<DependencyIcon sx={{ fontSize: "10px !important" }} />}
                label={`${linkTypeText} ${dep.issue.key}`}
                sx={{
                  height: 16,
                  fontSize: "8px",
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                  border: "1px solid #bbdefb",
                  mr: 0.5,
                  mb: 0.5,
                  "& .MuiChip-icon": {
                    fontSize: "8px",
                    color: "#1976d2",
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            </Tooltip>,
          )
        }
      })
    }

    if (dependencyInfo.blockedBy && dependencyInfo.blockedBy.length > 0) {
      dependencyInfo.blockedBy.forEach((dep) => {
        const dependencyKey = createDependencyKey(issue.id, dep.issue.id)

        if (!processedDependencies.has(dependencyKey)) {
          processedDependencies.add(dependencyKey)

          const linkTypeText =
            dep.linkType === "is blocked by"
              ? "blocked by"
              : dep.linkType === "blocks"
                ? "blocked by"
                : dep.linkType === "relates to"
                  ? "relates to"
                  : dep.linkType === "duplicates"
                    ? "duplicated by"
                    : dep.linkType === "is duplicated by"
                      ? "duplicated by"
                      : "blocked by"

          chips.push(
            <Tooltip
              key={`blockedBy-${dep.issue.id}`}
              title={`${linkTypeText} ${dep.issue.key}: ${dep.issue.summary}`}
              arrow
            >
              <Chip
                size="small"
                icon={<DependencyIcon sx={{ fontSize: "10px !important" }} />}
                label={`${linkTypeText} ${dep.issue.key}`}
                sx={{
                  height: 16,
                  fontSize: "8px",
                  bgcolor: "#fff3e0",
                  color: "#f57c00",
                  border: "1px solid #ffcc02",
                  mr: 0.5,
                  mb: 0.5,
                  "& .MuiChip-icon": {
                    fontSize: "8px",
                    color: "#f57c00",
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            </Tooltip>,
          )
        }
      })
    }

    if (dependencyInfo.relatesTo && dependencyInfo.relatesTo.length > 0) {
      dependencyInfo.relatesTo.forEach((dep) => {
        const dependencyKey = createDependencyKey(issue.id, dep.issue.id)

        if (!processedDependencies.has(dependencyKey)) {
          processedDependencies.add(dependencyKey)

          chips.push(
            <Tooltip
              key={`relatesTo-${dep.issue.id}`}
              title={`relates to ${dep.issue.key}: ${dep.issue.summary}`}
              arrow
            >
              <Chip
                size="small"
                icon={<DependencyIcon sx={{ fontSize: "10px !important" }} />}
                label={`relates to ${dep.issue.key}`}
                sx={{
                  height: 16,
                  fontSize: "8px",
                  bgcolor: "#f3e5f5",
                  color: "#7b1fa2",
                  border: "1px solid #ce93d8",
                  mr: 0.5,
                  mb: 0.5,
                  "& .MuiChip-icon": {
                    fontSize: "8px",
                    color: "#7b1fa2",
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            </Tooltip>,
          )
        }
      })
    }

    return chips.length > 0 ? <Box sx={{ display: "flex", flexWrap: "wrap", mt: 0.5 }}>{chips}</Box> : null
  }

  // Render issue row
  const renderIssueRow = (issue, level = 0, parentEndDate = null) => {
    const isEpic = Number(issue.issueType) === 2 || String(issue.issueType).toLowerCase() === "epic"
    const isStory = Number(issue.issueType) === 3 || String(issue.issueType).toLowerCase() === "story"
    const isExpanded = isEpic
      ? Boolean(expandedEpics[issue.id] || expandedEpics[String(issue.id)])
      : Boolean(expandedStories[issue.id] || expandedStories[String(issue.id)])
    const hasChildren = Boolean(issue.children && issue.children.length > 0)
    const position = calculateGanttPosition(issue, parentEndDate)
    const hasOverdue = hasOverdueIssues(issue, parentEndDate)

    const IssueIcon = ISSUE_TYPES[issue.issueType]?.icon || TaskIcon

    const handleToggle = (e) => {
      e?.stopPropagation?.()
      if (isEpic) {
        onToggleEpicExpansion(issue.id)
      } else {
        onToggleStoryExpansion(issue.id)
      }
    }

    return (
      <Box
        key={issue.id}
        sx={{
          display: "flex",
          borderBottom: "1px solid #e0e0e0",
          height: 96,
          bgcolor: hasOverdue ? "#ffebee" : "transparent",
          "&:hover": {
            bgcolor: hasOverdue ? "#ffcdd2" : "#f8fafc",
          },
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Issue details column */}
        <Box
          sx={{
            width: 400,
            padding: 2,
            display: "flex",
            alignItems: "center",
            borderRight: "2px solid #e0e0e0",
            paddingLeft: `${level * 24 + 16}px`,
            userSelect: "none",
          }}
        >
          {hasOverdue && <WarningIcon sx={{ color: "#d32f2f", fontSize: 18, mr: 1, flexShrink: 0 }} />}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: hasChildren ? "pointer" : "default",
              mr: 1.5,
              flexShrink: 0,
            }}
            onClick={hasChildren ? handleToggle : undefined}
            title={hasChildren ? (isExpanded ? "Click to collapse" : "Click to expand") : undefined}
          >
            <IssueIcon
              sx={{
                color: hasOverdue ? "#d32f2f" : ISSUE_TYPES[issue.issueType]?.color || "#1976d2",
                fontSize: 18,
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: hasOverdue ? "#d32f2f" : "inherit",
                fontWeight: "bold",
                mb: 0.5,
                cursor: "pointer",
                "&:hover": {
                  color: "#1976d2",
                  textDecoration: "underline",
                },
              }}
              onClick={(e) => {
                e.stopPropagation()
                onIssueNameClick(issue)
              }}
              title="Click to view details"
            >
              {issue.key} - {issue.summary}
            </Typography>

            {renderDependencyChips(issue)}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={ISSUE_TYPES[issue.issueType]?.name || "Issue"}
                size="small"
                sx={{
                  bgcolor: ISSUE_TYPES[issue.issueType]?.bgColor || "#f5f5f5",
                  color: ISSUE_TYPES[issue.issueType]?.color || "#666",
                  fontSize: "10px",
                  height: 20,
                }}
              />
              <Chip
                label={STATUS_NAMES[issue.status] || "Unknown"}
                size="small"
                sx={{
                  bgcolor: (STATUS_COLORS[issue.status] || "#9e9e9e") + "20",
                  color: STATUS_COLORS[issue.status] || "#9e9e9e",
                  fontSize: "10px",
                  height: 20,
                }}
              />
              {issue.assignee && (
                <Typography variant="caption" color="textSecondary" noWrap>
                  {issue.assigneeName || issue.assignee}
                </Typography>
              )}
            </Box>
          </Box>

          {hasChildren ? (
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{ ml: 1, p: 0.5, cursor: "pointer", zIndex: 3 }}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          ) : (
            (isEpic || isStory) && (
              <Box sx={{ width: 28, ml: 1 }} />
            )
          )}
          <IconButton size="small" onClick={(e) => onMenuClick(e, issue)} sx={{ ml: "auto", zIndex: 3 }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Gantt timeline column */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          {renderIssueBar(issue, position, parentEndDate)}
        </Box>
      </Box>
    )
  }

  return (
    <Paper
      sx={{
        overflow: "hidden",
        position: "relative",
        "& .gantt-row:hover .dependency-line": {
          opacity: 1,
          strokeWidth: 3,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", borderBottom: "2px solid #e0e0e0", bgcolor: "#f5f5f5" }}>
        <Box sx={{ width: 400, padding: 2, borderRight: "2px solid #e0e0e0" }}>
          <Typography variant="h6">Issues</Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            padding: 2,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <Typography variant="h6">Timeline</Typography>
        </Box>
      </Box>

      {/* Timeline Headers */}
      <Box sx={{ display: "flex", borderBottom: "1px solid #e0e0e0", bgcolor: "#fafafa" }}>
        <Box sx={{ width: 400, borderRight: "2px solid #e0e0e0" }} />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {timeColumns.map((column, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                padding: 1,
                textAlign: "center",
                borderRight: index < timeColumns.length - 1 ? "1px solid #e0e0e0" : "none",
                minWidth: timelineView === "weekly" ? "80px" : `${100 / timeColumns.length}%`,
                maxWidth: timelineView === "weekly" ? "120px" : "none",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  fontSize: timelineView === "weekly" ? "10px" : "12px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {column.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Issue Rows Container with SVG Overlay */}
      <Box sx={{ position: "relative" }}>
        {/* Issue Rows */}
        <Box ref={scrollContainerRef} sx={{ maxHeight: "70vh", overflow: "auto" }} onScroll={handleScroll}>
          {organizedIssues.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="textSecondary">
                No issues found for the selected filters.
              </Typography>
            </Box>
          ) : (
            organizedIssues.map((epic) => {
              const isEpicExpanded = Boolean(expandedEpics[epic.id] || expandedEpics[String(epic.id)])
              return (
                <Box key={epic.id}>
                  {/* Epic Row */}
                  {renderIssueRow(epic, 0)}

                  {/* Epic Children */}
                  <Collapse in={isEpicExpanded} timeout="auto" unmountOnExit>
                    {epic.children?.map((child) => {
                      const isChildStory = Number(child.issueType) === 3 || Number(child.issueType) === 4 || String(child.issueType).toLowerCase() === "story"
                      const isChildExpanded = Boolean(expandedStories[child.id] || expandedStories[String(child.id)])
                      return (
                        <Box key={child.id}>
                          {/* Story/Task/Bug Row */}
                          {renderIssueRow(child, 1, epic.endDate)}

                          {/* Story and Task Children */}
                          {isChildStory &&
                            child.children &&
                            child.children.length > 0 && (
                              <Collapse in={isChildExpanded} timeout="auto" unmountOnExit>
                                {child.children.map((grandChild) => renderIssueRow(grandChild, 2, child.endDate))}
                              </Collapse>
                            )}
                        </Box>
                      )
                    })}
                  </Collapse>
                </Box>
              )
            })
          )}
        </Box>

        {/* SVG overlay for dependency lines */}
        <svg
          ref={svgRef}
          style={{
            position: "absolute",
            top: 0,
            left: 400,
            width: "calc(100% - 400px)",
            height: "100%",
            pointerEvents: "none",
            zIndex: 10,
            overflow: "visible",
          }}
        >
          <defs>
            <marker
              id="professional-arrowhead"
              markerWidth="8"
              markerHeight="6"
              refX="7"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <polygon points="0 0, 8 3, 0 6" fill="#666" opacity="0.8" />
            </marker>
          </defs>

          {/* Use DependencyLines component */}
          <DependencyLines
            key={`dependency-lines-${svgWidth}-${scrollTop}-${organizedIssues.length}`}
            dependencies={dependencies}
            organizedIssues={organizedIssues}
            expandedEpics={expandedEpics}
            expandedStories={expandedStories}
            timeRange={timeRange}
            timelineView={timelineView}
            svgWidth={svgWidth}
            taskDetailsPanelOpen={taskDetailsPanelOpen}
            scrollTop={scrollTop}
            calculateGanttPosition={calculateGanttPosition}
            rowHeight={96}
            headerHeight={0}
            sidebarOpen={sidebarOpen}
          />
        </svg>
      </Box>
    </Paper>
  )
}

export default GanttChart